import { useMemo } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import type { Department } from '../../../api/types/corporate';

/** Read-only visual org-chart rendering of the department hierarchy — an additional view
 *  alongside DepartmentListPage's flat indented table (see that file's flattenTree for the
 *  DataGrid/tree-view incompatibility rationale; this component is built from plain MUI
 *  primitives for the same reason).
 *
 *  Connector lines are drawn with the classic CSS-only "tree of nested <ul>/<li>" trick:
 *  each <li> draws two half-width top-border segments (via ::before/::after) that meet at
 *  its horizontal center, and each nested <ul> draws a short vertical drop from its parent.
 *  Adjacent siblings' half-lines join into one continuous line above the row, and the first/
 *  last child in a row trims its outer half so the line doesn't overshoot the group — all
 *  without knowing any node's pixel width up front, so it holds up for arbitrarily wide/deep
 *  trees. No canvas/SVG dependency involved.
 */

interface DepartmentNode {
  department: Department;
  children: DepartmentNode[];
}

/** Nests the parent-linked flat list into a tree. Mirrors DepartmentListPage's flattenTree
 *  orphan handling: a row whose parentDepartmentId points at a missing/already-visited id is
 *  still surfaced, as an extra root, rather than silently dropped. */
function buildTree(rows: Department[]): DepartmentNode[] {
  const byParent = new Map<number | null, Department[]>();
  for (const row of rows) {
    const key = row.parentDepartmentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row);
  }
  const visited = new Set<number>();

  function build(parentId: number | null): DepartmentNode[] {
    const children = (byParent.get(parentId) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    const nodes: DepartmentNode[] = [];
    for (const child of children) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      nodes.push({ department: child, children: build(child.id) });
    }
    return nodes;
  }

  const roots = build(null);
  for (const row of rows) {
    if (!visited.has(row.id)) {
      visited.add(row.id);
      roots.push({ department: row, children: build(row.id) });
    }
  }
  return roots;
}

const CONNECTOR_COLOR = 'divider' as const;

const treeListSx = {
  display: 'flex',
  position: 'relative',
  m: 0,
  p: 0,
  pt: '20px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '50%',
    width: 0,
    height: '20px',
    borderLeft: '2px solid',
    borderLeftColor: CONNECTOR_COLOR,
  },
} as const;

const treeItemSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  listStyle: 'none',
  position: 'relative',
  px: 1.5,
  pt: '20px',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: '50%',
    width: '50%',
    height: '20px',
    borderTop: '2px solid',
    borderTopColor: CONNECTOR_COLOR,
  },
  '&::after': {
    right: 'auto',
    left: '50%',
    borderLeft: '2px solid',
    borderLeftColor: CONNECTOR_COLOR,
  },
  '&:only-child': { pt: 0 },
  '&:only-child::before, &:only-child::after': { display: 'none' },
  '&:first-of-type::before': { borderTopColor: 'transparent' },
  '&:last-of-type::after': { borderLeftColor: 'transparent' },
  '&:last-of-type::before': {
    borderRight: '2px solid',
    borderRightColor: CONNECTOR_COLOR,
    borderTopRightRadius: 6,
  },
  '&:first-of-type::after': { borderTopLeftRadius: 6 },
} as const;

function DepartmentNodeCard({
  department,
  onClick,
}: {
  department: Department;
  onClick?: (department: Department) => void;
}) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick ? () => onClick(department) : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        px: 2,
        py: 1,
        minWidth: 150,
        maxWidth: 220,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': onClick ? { boxShadow: 3, borderColor: 'primary.main' } : undefined,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center', wordBreak: 'break-word' }}>
        {department.name}
      </Typography>
      <Chip
        size="small"
        color={department.isActive ? 'success' : 'default'}
        label={department.isActive ? 'Aktif' : 'Pasif'}
      />
    </Paper>
  );
}

function OrgChartNode({
  node,
  onClick,
}: {
  node: DepartmentNode;
  onClick?: (department: Department) => void;
}) {
  return (
    <Box component="li" sx={treeItemSx}>
      <DepartmentNodeCard department={node.department} onClick={onClick} />
      {node.children.length > 0 && (
        <Box component="ul" sx={treeListSx}>
          {node.children.map((child) => (
            <OrgChartNode key={child.department.id} node={child} onClick={onClick} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export function DepartmentOrgChartView({
  rows,
  onNodeClick,
}: {
  rows: Department[];
  onNodeClick?: (department: Department) => void;
}) {
  const roots = useMemo(() => buildTree(rows), [rows]);

  if (roots.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        Kayıtlı departman bulunamadı.
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto', width: '100%', py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, width: 'fit-content', mx: 'auto' }}>
        {roots.map((root) => (
          <Box key={root.department.id} component="ul" sx={{ display: 'flex', m: 0, p: 0 }}>
            <OrgChartNode node={root} onClick={onNodeClick} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
